import { useDimensionLabel, useOptionalT } from '@/lib/i18n';
import type { Localized } from '@/lib/api';
import { useOptionalBandLabel } from '@/features/result/labels';
import type { DisplayDimension } from '@/features/result/viz';

/**
 * headline.ts — the last untranslatable English string on a result (N5).
 *
 * `display.headline.value` is a sentence a plugin assembled in English —
 * `"SEA — High"`, `"openness highest, emotionalStability lowest"`. It is the
 * `<PageHeader>` title of the result screen and the chart's description, which
 * makes it the most prominent string in the product, and on an Arabic-first
 * youth persona it was the one an Arabic reader could not read. PROGRESS §33b
 * recorded it and correctly said fixing it needed a contract field.
 *
 * ### What the contract added, and what it deliberately did not
 *
 * §33b's standing rule is that **the wire stays English and the console
 * translates**. So `value` is unchanged — contract v0 publishes it and an
 * integrator stores it verbatim — and what N5 adds is `key` + `vars`: a stable
 * template id, and its interpolations. The sentence is not translated on the
 * wire; it is made *translatable*.
 *
 * ### `vars` carry KEYS, and that is the whole reason this file is not a one-liner
 *
 * `{ highest: 'openness' }`, never `{ highest: 'Openness' }`. A var carrying the
 * English label would leave the console able only to re-print what it was
 * handed, which is the defect N5 exists to fix. So every var is resolved before
 * it reaches the sentence: a dimension key through `useDimensionLabel`, a band
 * through the band lookup. Interpolating them raw would render
 * "openness highest, emotionalStability lowest" inside an otherwise Arabic
 * sentence — a translation that is somehow worse than the English it replaced.
 *
 * ### Three steps down, exactly like `useDimensionLabel`
 *
 *   1. **The catalogue**, `headline.*` in `lib/strings/*` — the only step that
 *      can be Arabic, and where the three launch plugins' sentences live.
 *   2. **`headline.value`** — English, but a phrase a human wrote. A plugin
 *      registered tomorrow emits a key this catalogue has never seen, and its
 *      result must still read as a report.
 *   3. **Never the key.** There is no third step: a raw
 *      `headline.riasec.code_band` shouted at a counsellor is the one outcome
 *      worse than English.
 */

/** `DisplayProjection.headline`, as `lib/api.ts` mirrors it. */
export interface DisplayHeadline {
  value: string;
  label: Localized;
  key?: string;
  vars?: Record<string, string>;
}

/**
 * One var, resolved from a key into words the operator reads.
 *
 * The order is dimension-then-band and the fallback is the value itself, which
 * covers the third kind of var: data. RIASEC's `hollandCode` is `"SEA"` — not a
 * dimension, not a band, and not ours to translate. It reaches the sentence
 * unchanged, which is correct; a Holland code is the same in both languages.
 */
function useHeadlineVar(): (value: string, labels: Map<string, Localized>) => string {
  const dimensionLabel = useDimensionLabel();
  const bandLabel = useOptionalBandLabel();

  return (value, labels) => {
    // `useDimensionLabel` is catalogue → the envelope's own label → the key. Its
    // second step is what makes this work for a model registered tomorrow, and
    // a return of the key itself means it found neither.
    const dimension = dimensionLabel(value, labels.get(value));
    if (dimension !== value) return dimension;
    return bandLabel(value) ?? value;
  };
}

/**
 * The headline, in the operator's language.
 *
 * Takes the projection's `dimensions` as well, because they carry the English
 * labels a dimension var falls back to when the catalogue has never heard of the
 * key — the same source `useDimensionLabel` reads on every chart axis.
 */
export function useHeadline(): (
  headline: DisplayHeadline,
  dimensions: DisplayDimension[],
) => string {
  const optionalT = useOptionalT();
  const resolveVar = useHeadlineVar();

  return (headline, dimensions) => {
    if (!headline.key) return headline.value;

    const labels = new Map(dimensions.map((d) => [d.key, d.label]));
    const vars = Object.fromEntries(
      Object.entries(headline.vars ?? {}).map(([name, value]) => [name, resolveVar(value, labels)]),
    );

    return optionalT(headline.key, vars) ?? headline.value;
  };
}
